import { useEffect, useId, useRef } from 'react';
import DrawerHeader from '../studio/DrawerHeader';
import { FOCUSABLE_SELECTOR, focusableWithin, wrapTabFocus } from '../../lib/focusTrap';
import { c } from '../../styles/theme';
import type { PublisherFinancialExplainer } from '../../types/domain';

export interface PublisherFinancialExplainerDrawerProps {
  explainer: PublisherFinancialExplainer;
  onClose: () => void;
}

export default function PublisherFinancialExplainerDrawer({
  explainer,
  onClose,
}: PublisherFinancialExplainerDrawerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const headingId = useId();

  useEffect(() => {
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = rootRef.current;
    if (root) {
      const firstFocusable =
        focusableWithin(root)[0] ?? root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? root;
      firstFocusable.focus();
    }
    return () => {
      const target = previouslyFocused.current;
      previouslyFocused.current = null;
      if (target?.isConnected) target.focus();
    };
  }, []);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      wrapTabFocus(root, event);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  return (
    <>
      <div className="lyx-drawer-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={rootRef}
        className="lyx-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        style={{
          borderLeft: `1px solid ${c.border}`,
          background: c.bg,
          padding: 16,
          boxShadow: '-14px 0 42px rgba(0,0,0,0.28)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div id={headingId} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
          {explainer.title}
        </div>
        <DrawerHeader title={explainer.title} backLabel={explainer.title} onBack={onClose} onClose={onClose} />

        {explainer.intro ? (
          <section
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              background: c.surface1,
              padding: '14px 16px',
            }}
          >
            <p style={{ margin: 0, fontSize: 12, color: c.text1, lineHeight: 1.65 }}>{explainer.intro}</p>
          </section>
        ) : null}

        <div style={{ display: 'grid', gap: 10 }}>
          {explainer.sections.map((section) => (
            <section
              key={section.title}
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 12,
                background: c.surface1,
                padding: '14px 16px',
              }}
            >
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 11,
                  color: c.text2,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                {section.title}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: c.text1, lineHeight: 1.65 }}>{section.body}</p>
            </section>
          ))}
        </div>

        {explainer.footerNote ? (
          <section
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              background: c.surface2,
              padding: '12px 14px',
            }}
          >
            <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.6 }}>{explainer.footerNote}</p>
          </section>
        ) : null}
      </div>
    </>
  );
}
